import moment from 'moment-timezone';
import { findTimeZone } from 'timezone-support/index-1900-2050';

export const getDefaultTimeZone = (time_zone = '') => {
  const browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  let timeZone;
  if (time_zone) {
    // If a specific timezone is provided, preserve it
    timeZone = time_zone;
  } else {
    // Only use findTimeZone for browser timezone to get canonical name
    timeZone = findTimeZone(browserTimeZone)?.name || browserTimeZone;
  }

  const offset = moment.tz(timeZone).format('Z'); // Get the GMT offset
  const formattedOffset = offset.startsWith('+')
    ? `(GMT${offset})`
    : `(GMT${offset})`;

  return { label: `${formattedOffset} ${timeZone}`, key: timeZone };
};

/**
 * The first selectable slot strictly after "now" in `timeZone`, on a
 * `stepInMinutes` grid.
 *
 * Two things the obvious `Math.ceil(minutes / step) * step` gets wrong, both of
 * which leave the picker holding a time that has already passed:
 *   - on an exact boundary (:00, :30) ceil returns the current minute itself,
 *     so the "future" default is now — the option filter then drops it and the
 *     select falls back to its placeholder;
 *   - past the last slot of the day it overflows to 60, which moment rolls into
 *     the next day's 00:00 while the caller keeps today's date.
 * So step forward from the floor, and hand back the day the slot landed on.
 *
 * `date` is browser-local midnight of that day, matching how the field stores
 * a date: a wall-clock day, re-anchored with `.tz(key, true)` when it is read.
 */
export const getNextSelectableSlot = (
  timeZone?: string,
  stepInMinutes = 30
): { date: Date; time: string } => {
  const now = moment().tz(timeZone || moment.tz.guess());
  const slot = now
    .clone()
    .startOf('minute')
    .minutes(Math.floor(now.minutes() / stepInMinutes) * stepInMinutes)
    .add(stepInMinutes, 'minutes');

  return {
    date: new Date(slot.year(), slot.month(), slot.date()),
    time: slot.format('HH:mm'),
  };
};

export const convertDateFormatForDatePicker = (dateStr) => {
  if (!dateStr) return '';
  const [day, month, year] = dateStr.includes('/')
    ? dateStr.split('/')
    : dateStr.includes('-')
      ? dateStr.split('-')
      : dateStr.split(' ');
  return `${month}/${day}/${year}`;
};
